import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import CurrentDashboard from './CurrentDashboard';
// Removed: DashboardSnapshot - functionality merged into CurrentDashboard
import { useSnapshotMonths } from '../hooks/useSnapshotMonths';

/**
 * DashboardRouter - Enhanced CurrentDashboard with automatic current/snapshot switching
 * Based on URL parameters (?snapshot=YYYY-MM) or month selection
 * All functionality now handled by unified CurrentDashboard component
 */
const DashboardRouter = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [error, setError] = useState(null);

  // Parse URL parameters for legacy support
  const searchParams = new URLSearchParams(location.search);
  const snapshotMonth = searchParams.get('snapshot') || searchParams.get('snapshotMonth');

  // Use optimized hook for snapshot months
  const { 
    months: availableSnapshots, 
    loading, 
    error: snapshotError,
    refetch: refetchSnapshots 
  } = useSnapshotMonths({
    autoFetch: true,
    debounceDelay: 500,
    forceRefresh: false
  });

  // Handle snapshot error
  useEffect(() => {
    if (snapshotError) {
      setError('Failed to load snapshot information');
    } else {
      setError(null);
    }
  }, [snapshotError]);

  /**
   * Handle navigation - now just changes URL parameter for legacy support
   * CurrentDashboard handles the actual data switching internally
   */
  const handleNavigateToSnapshot = (month) => {
    if (month) {
      navigate(`/?snapshot=${month}`);
    } else {
      navigate('/');
    }
  };

  /**
   * Loading state
   */
  if (loading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '100vh',
          gap: 2
        }}
      >
        <CircularProgress size={40} />
        <Typography variant="body2" color="text.secondary">
          Loading dashboard...
        </Typography>
      </Box>
    );
  }

  /**
   * Error state
   */
  if (error) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '100vh',
          gap: 2
        }}
      >
        <Typography variant="h6" color="error">
          {error}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Falling back to current dashboard...
        </Typography>
      </Box>
    );
  }

  /**
   * Always render CurrentDashboard - it now handles both current and snapshot data internally
   * The unified CurrentDashboard automatically switches between current/snapshot based on month
   */
  return (
    <CurrentDashboard 
      availableSnapshots={availableSnapshots}
      onSnapshotSelect={handleNavigateToSnapshot}
      initialSelectedMonth={snapshotMonth} // Pass URL parameter as initial selection
    />
  );
};

export default DashboardRouter; 